// src/components/order/validation.ts
import { i18n } from "@dehub/theme";
import * as yup from "yup";
import { citizenRegNumber, taxNumber } from "../../utils";
import { OrderFormMode, OrderFormSide } from "./types";

export const getValidationSchema = (mode: OrderFormMode, side: OrderFormSide = "supplier") => {
  const r = () => i18n.t("common:validation.required");
  const s = () => i18n.t("common:validation.selectRequired");
  const tl = () => i18n.t("common:validation.tooLong");

  const baseSchema = {
    regNumber: yup.string().when(["partnerType"], (partnerType) => {
      const t = Array.isArray(partnerType) ? partnerType[0] : partnerType;
      // Known individual (хувь хүн) — accept the personal register format only.
      if (typeof t === "string" && t === "CITIZEN") {
        return yup
          .string()
          .matches(citizenRegNumber, () => i18n.t("common:validation.invalidFormat"))
          .required(r);
      }
      // Company OR unknown partnerType — accept EITHER a 7-digit company ТТД or an
      // individual register. partner.type is a nullable free string that the backend
      // may default to "COMPANY", so never force a ТТД on a хувь хүн customer; doing
      // so blocks individuals from submitting orders.
      return yup
        .string()
        .test(
          "reg-or-ttd",
          () => i18n.t("common:validation.invalidTaxId"),
          (value) => !value || taxNumber.test(value)
        )
        .required(r);
    }),
    partnerName: yup.string().required(r),
    supplierId: yup.string().required(r),
    deliveryType: yup.string().required(s).nullable(),
    deliveryDate: yup.string().when(["deliveryType"], (deliveryType) => {
      const t = Array.isArray(deliveryType) ? deliveryType[0] : deliveryType;
      if (typeof t === "string" && t === "CUSTOM_DATE") {
        return yup.string().required(r);
      }
      return yup.string().nullable();
    }),
    deliveryCheck: yup.string().when(["deliveryType"], (deliveryType) => {
      const t = Array.isArray(deliveryType) ? deliveryType[0] : deliveryType;
      if (typeof t === "string" && t === "DEFAULT_DATE") {
        return yup.string().required(r);
      }
      return yup.string().nullable();
    }),
    lines: yup.array().of(
      yup.object({
        quantity: yup.number().min(1, r).required(r),
      })
    ),
    attachments: yup.array().of(
      yup.object({
        name: yup.string().required(r).max(255, tl),
        url: yup.string().required(r).url(() => i18n.t("common:validation.invalidUrl")).max(500, tl),
        description: yup.string().required(r).max(255, tl),
      })
    ),
  };

  // Detail mode - no validation needed
  if (mode === "detail") {
    return yup.object({});
  }

  // Edit mode - requires staff and branch
  if (mode === "edit") {
    return yup.object({
      ...baseSchema,
      receiverStaffId: yup.string().required(r),
      receiverBranchId: yup.string().required(r),
    });
  }

  // Create mode
  const createSchema: any = {
    ...baseSchema,
    additionalLines: yup.array().of(
      yup.object({
        name: yup.string().required(r).max(255, tl),
        unit: yup.string().required(r).max(100, tl),
        price: yup.number().min(1, r).required(r),
        quantity: yup.number().min(1, r).required(r),
        discountType: yup.string().optional().nullable(),
        discountValue: yup.number().optional().nullable(),
      })
    ),
  };

  // Buyer-side create requires editable receiver staff + branch
  if (side === "buyer") {
    createSchema.receiverStaffId = yup.string().required(r);
    createSchema.receiverBranchId = yup.string().required(r);
  }

  return yup.object(createSchema);
};
